import { useEffect, useRef } from "react";
import { useAuth } from "../auth/AuthProvider";
import { api } from "../lib/api";
import { deserializeCart, mergeCarts } from "./cartReducer";
import { useCart } from "./CartProvider";

const SAVE_DELAY_MS = 1200;

/**
 * Keeps a signed-in customer's cart on the server: on sign-in the saved cart is merged into this
 * device's, then every change is saved (debounced). Guests keep the cart in the browser only.
 */
export function CartSync() {
  const { user } = useAuth();
  const cart = useCart();
  const syncedFor = useRef<string | null>(null);
  const lastSaved = useRef<string>("");

  // Sign-in (or a different account): merge once.
  useEffect(() => {
    if (!user) {
      syncedFor.current = null;
      return;
    }
    if (syncedFor.current === user.id) return;
    let alive = true;
    api
      .cart()
      .then((remote) => {
        if (!alive) return;
        const merged = mergeCarts(cart.state, deserializeCart(JSON.stringify({ lines: remote.lines })));
        lastSaved.current = JSON.stringify(remote.lines);
        syncedFor.current = user.id;
        cart.replace(merged);
      })
      .catch(() => {
        // Offline or not allowed (e.g. "Próximamente"): the local cart keeps working.
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Save changes once merged.
  useEffect(() => {
    if (!user || syncedFor.current !== user.id) return;
    const body = JSON.stringify(cart.state.lines);
    if (body === lastSaved.current) return;
    const t = setTimeout(() => {
      api
        .saveCart(cart.state.lines)
        .then(() => (lastSaved.current = body))
        .catch(() => {});
    }, SAVE_DELAY_MS);
    return () => clearTimeout(t);
  }, [cart.state, user]);

  return null;
}
